import {
	OnDestroy,
	TemplateRef,
	ViewContainerRef,
	Component,
	ViewChild,
	ChangeDetectionStrategy,
	ViewEncapsulation,
	ChangeDetectorRef,
	Type,
	ComponentRef,
	Injector,
	AfterViewInit,
	ElementRef,
	Input
} from '@angular/core';
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { Subject } from 'rxjs';
import { A11yModule } from '@angular/cdk/a11y';
import { OverlayModule } from '@angular/cdk/overlay';
import { PortalModule } from '@angular/cdk/portal';
import { TranslatePipe } from '@ngx-translate/core';
import * as Color from 'color';

export type PopoverPosition = 'above' | 'below' | 'before' | 'after' | 'left' | 'right';
export type PopoverTrigger = 'hover' | 'click' | 'manual';
export type PopoverScrollStrategy = 'close' | 'reposition' | 'noop';

@Component({
	selector: 'go-cdk-popover-content',
	imports: [NgClass, NgTemplateOutlet, A11yModule, OverlayModule, PortalModule, NgStyle, TranslatePipe],
	templateUrl: 'go-cdk-popover-content.component.html',
	styleUrls: ['go-cdk-popover-content.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None // make styles global
})
export class GoCdkPopoverContentComponent<T = any> implements AfterViewInit, OnDestroy {
	@ViewChild('componentTemplate', { read: ViewContainerRef, static: true })
	private componentViewContainer!: ViewContainerRef;

	@ViewChild('popoverPanel', { static: true })
	private popoverPanel!: ElementRef<HTMLElement>;

	private componentRef?: ComponentRef<T>;
	private destroyed$: Subject<void> = new Subject<void>();
	private resolvedBg!: string;
	private resolvedFg!: string;

	public closed = new Subject<void>();
	public defaultBgColor = '#545454';
	@Input() public contentTemplate?: TemplateRef<unknown>;
	@Input() public contentText?: string;
	@Input() public contentComponent?: Type<T>;
	@Input() public context = {};
	@Input() public panelClass: string | string[] = '';
	@Input() public title?: string;
	@Input() public titleIcon?: string;
	@Input() public titleIconColor?: string;
	@Input() public showCloseButton = false;
	@Input() public closeButtonAriaLabel = 'Close popover';
	@Input() public id?: string;
	@Input() public isVisible = true;
	@Input() public showArrow = false;
	@Input() public arrowPosition = '';
	@Input() public arrowOffsetX = 0;
	@Input() public arrowOffsetY = 0;
	@Input() public bgColor?: string;
	@Input() public isClickTriggered = false;
	@Input() public isInteractive = false;
	@Input() public shouldTrapFocus = false;
	@Input() public shadow = false;
	@Input() public maxContentHeight?: number | string;

	constructor (
		private cdr: ChangeDetectorRef,
		private injector: Injector
	) {}

	public close () {
		this.closed.next();
	}

	public stopEventPropagation (event: Event): void {
		event.stopPropagation();
	}

	public updateContent () {
		this.cdr.markForCheck();
	}

	public ngOnDestroy () {
		if (this.componentRef) {
			this.componentRef.destroy();
		}
		this.closed.complete();
		this.destroyed$.next();
		this.destroyed$.complete();
	}

	public ngAfterViewInit () {
		this.createComponent();

		this.resolvedBg = this.resolveCssColor(this.bgColor);
		this.resolvedFg = this.pickTextColor(this.resolvedBg);
		const el = this.popoverPanel.nativeElement;
		el.style.setProperty('--go-cdk-popover-bg-runtime', this.resolvedBg);
		el.style.color = this.resolvedFg;
		this.applyMaxHeightVar();
		this.cdr.markForCheck();
	}

	public get panelStyle () {
		const styles = {
			color: '',
			boxShadow: ''
		};

		if (this.bgColor) {
			styles.color = this.bestContrast(this.bgColor); // set foreground
		}

		if (this.shadow) {
			styles.boxShadow = '-4px 2px 12px rgba(0,0,0,0.2), 2px 2px 8px rgba(0,0,0,0.1)';
		}

		return styles;
	}

	public get arrowStyle () {
		const styles: Record<string, string> = {};

		if (this.arrowPosition === 'arrow-above' || this.arrowPosition === 'arrow-below') {
			styles.transform = `translateX(calc(-50% + ${this.arrowOffsetX}px))`;
		} else if (this.arrowPosition === 'arrow-before' || this.arrowPosition === 'arrow-after' ||
				this.arrowPosition === 'arrow-left' || this.arrowPosition === 'arrow-right') {
			styles.transform = `translateY(calc(-50% + ${this.arrowOffsetY}px))`;
		}

		return styles;
	}

	private createComponent () {
		if (!this.contentComponent || this.componentRef) return;

		this.componentRef = this.componentViewContainer.createComponent<T>(this.contentComponent, {
			injector: this.injector
		});

		this.cdr.markForCheck();
	}

	private resolveCssColor (raw: string): string {
		const host = this.popoverPanel.nativeElement;
		if (!raw) {
			return this.defaultBgColor;
		}
		if (!raw.startsWith('var(')) {
			return raw.trim();
		}
		// CSS Var
		const token = raw.slice(4, -1).trim();
		const fromCss = getComputedStyle(host).getPropertyValue(token).trim();
		return fromCss || this.defaultBgColor;
	}

	private bestContrast (raw: string): string {
		return this.pickTextColor(this.resolveCssColor(raw));
	}

	private pickTextColor (bg: string): string {
		const lightText = getComputedStyle(document.documentElement).getPropertyValue('--color-off-white').trim();
		const darkText = getComputedStyle(document.documentElement).getPropertyValue('--color-emperor').trim();

		const c = Color(bg);
		return c.contrast(Color(lightText)) >= c.contrast(Color(darkText))
			? lightText
			: darkText;
	}

	private toCssSize (v?: number | string | null): string | null {
		if (v == null || v === '') return null;
		if (typeof v === 'number') {
			return `${v}px`;
		}
		const stringValue = v.toString().trim();
		if (/^\d+(\.\d+)?$/.test(stringValue)) {
			// numeric string without units, add 'px'
			return `${stringValue}px`;
		}

		// (already has units like 'px', '%', 'em', etc.)
		return stringValue;
	}

	private applyMaxHeightVar () {
		const el = this.popoverPanel.nativeElement;
		const val = this.toCssSize(this.maxContentHeight);
		if (val) {
			el.style.setProperty('--go-cdk-popover-max-h', val);
			el.classList.add('has-scroll-cap');
		} else {
			el.style.removeProperty('--go-cdk-popover-max-h');
			el.classList.remove('has-scroll-cap');
		}
	}
}
